import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";

import ScreenContainer from "../../Components/ScreenContainer";
import { OuterBoxV2, InnerBox } from "../../Components/CustomBox";
import Loader from "../../Helper/Loaders/Loader";
import GenericDraggableTableView from "../../Features/Table/GenericDraggableTable";
import PageHeaderKPI from "../../Features/PageHeader/PageHeaderKPI";
import TabWithColorDigitalTwins from "../../Features/Tabs/TabWithColorDigitalTwins";
import BasicTabPanel from "../../Features/Tabs/BasicTabPanel";
import { useTranslation } from "react-i18next";
import DeviceIDLink from "../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";
import ProgressBarColumnView from "../../Features/Table/GenericDraggableTable/Renderers/columnTypes/ProgressBarColumnView";
import { getColumnsConfig } from "../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { getDigitalTwinList, getDigitalTwinData } from "../../Api/DigitalTwinListApi";
import { getPagesMetaData } from "../../Api/Api";
import AppCache from "../../Helper/Common/AppCache";
import CODE from "../../Static/Constants/StatusCodes";
import {
    SetDigitalTwinsPageNumber,
    SetDigitalTwinsTotal,
    SetDigitalTwinsColumns,
} from "../../Actions/DigitalTwinsList";
import { SetSessionExpired } from "../../Actions";
import { MAX_COLUMN_SELECTION_COUNT } from "../../Helper/DigitalTwin/DataFile";
import { fetchFiltersDataObject } from "../../Helper/Table/TableHelper";
import {
    extractPartialConnectivityParams,
    getPartialConnectivityFilterKeys,
} from "../../Helper/Filters/FilterTimeRangeExtractor";
import { decryptTheParams, encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { getSOHColor } from "../../Helper/BatteryHealth/BatteryHealthHelper";
import { getOrganizationLangSettings } from "../../Helper/Organization/Functions";
import useCustomDashboardGlobalFilter from "../../Hooks/useCustomDashboardGlobalFilter";
import GlobalFilter from "../Overview/components/GlobalFilter";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import { usePermissions } from "../../Context/PermissionContext";

const COLUMN_SETTING_STORAGE_KEY = "CUSTOM_INVENTORY_COLUMN_SETTINGS";

/**
 * CustomInventoryPage — inventory page for the Custom Observability Platform.
 *
 * Column definitions, column order, filters, and searchable fields all come from
 * customDashboards[activeKey].inventory.digitalTwinList.table.digitalTwinListTable
 * stored in AppCache during login.
 *
 * Differences from the Coulomb inventory (DigitalTwinListTab):
 *   - Single tab (no battery configs or unlinked IoT tabs)
 *   - No Add / Edit actions (shouldShowActionMenu always returns false)
 *   - Download is enabled; Email is hidden (showEmailComposer={false})
 *   - No Operational Status update action
 *   - Column/filter config sourced from customObservabilityConfig, not from API
 */
function CustomInventoryPage() {
    const { t } = useTranslation(['inventory']);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const queryParams = decryptTheParams();
    const orgLangSettings = getOrganizationLangSettings();

    // ─── pagesContent for PagesContentContext (entity labels) ────────────────
    // Cached in AppCache to avoid a redundant network call when navigating
    // between custom overview and custom inventory within the same session.
    const [pageContent, setPageContent] = useState({ mounted: false });
    const [pageContentLoading, setPageContentLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            const cached = AppCache.getItem("fleetHomePageContent");
            if (cached) {
                setPageContent(cached);
                setPageContentLoading(false);
                return;
            }
            const res = await getPagesMetaData("fleetHomePage");
            if (res?.responseStatus?.code === CODE.SUCCESS) {
                const data = res.response?.response;
                AppCache.setItem("fleetHomePageContent", data);
                setPageContent(data);
            }
            setPageContentLoading(false);
        };
        load();
    }, []);

    // ─── Custom inventory config from AppCache ────────────────────────────────
    // Read once at mount using a lazy useState initializer.
    // AppCache.getItem() calls JSON.parse() on every read — plain const reads
    // would produce new object references on every render, causing the
    // tableConfig → allColumns useMemo → useEffect([allColumns]) chain to fire
    // on every render and create an infinite setState loop.
    const [tableConfig] = useState(() => {
        const cfg = AppCache.getItem("customObservabilityConfig") || {};
        return cfg.inventory?.digitalTwinList?.table?.digitalTwinListTable || {};
    });

    // ─── Global filter (fleet + location init handled by shared hook) ─────────
    const {
        globalFilters,
        globalFilterConfig,
        allFleets,
        allOnboardingLocationsHierarchy,
        canUseGlobalAdvancedFilter,
        handleFleetAndLocationChange,
    } = useCustomDashboardGlobalFilter();

    const { hasPermission, isRbacV2Enabled } = usePermissions();

    const shouldShowColumn = useCallback((config) => {
        const codes = config?.controlledByFeatureCodes;
        if (!codes?.length) return true;
        return !isRbacV2Enabled() || codes.every((code) => hasPermission(code, "view"));
    }, [hasPermission, isRbacV2Enabled]);

    // ─── Pagination (shared Redux state with Coulomb inventory) ───────────────
    const pageNumber = useSelector((state) => state.DigitalTwinsPageNumber.value);
    const rowsPerPage = useSelector((state) => state.DigitalTwinsRowsPerPage.value);
    const total = useSelector((state) => state.DigitalTwinsTotal.value);

    // ─── Table state ──────────────────────────────────────────────────────────
    const [isLoading, setIsLoading] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [filters, setFilters] = useState({});
    const [selectedFilters, setSelectedFilters] = useState(() => {
        try { return queryParams.filters ? JSON.parse(queryParams.filters) : {}; }
        catch { return {}; }
    });
    const [sorting, setSorting] = useState([]);
    const [searchTerm, setSearchTerm] = useState({ selectedDevices: [], selectedSearchColumn: null });
    const [columns, setColumns] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);
    const [columnOrder, setColumnOrder] = useState([]);
    const [visibleColumnKeys, setVisibleColumnKeys] = useState([]);
    const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
    const [totalSelectedRowBatteryIds, setTotalSelectedRowBatteryIds] = useState(new Set());
    const [isSelectAll, setIsSelectAll] = useState(false);
    const [unfilteredTotal, setUnfilteredTotal] = useState(null);
    const lastFetchParams = useRef(null);

    // ─── Actions ──────────────────────────────────────────────────────────────
    const onBatteryIDClick = useCallback((record) => {
        encryptTheParams(
            { batteryID: record.batteryID || record.id },
            navigate,
            false,
            "/custom-observability/specificBattery",
            true
        );
    }, [navigate]);

    // ─── Custom renderers (same as Coulomb Inventory.jsx) ────────────────────
    const customRenderers = useMemo(() => ({
        "digital-twin-list-series-parallel": (record, config) => {
            const level = config?.seriesParallelConfigLevel;
            const value = level === "pack"
                ? `${record?.seriesPacks}S / ${record?.parallelPacks}P`
                : level === "module"
                    ? `${record?.seriesModulesInPack}S / ${record?.parallelModulesInPack}P`
                    : `${record?.seriesModulesInPack * record?.seriesCellsInModule}S / ${record?.parallelModulesInPack * record?.parallelCellsInModule}P`;
            return <span>{value}</span>;
        },
        "device-id-link": (record, config) => (
            <DeviceIDLink
                record={record}
                config={config}
                onTextClick={() => onBatteryIDClick(record)}
            />
        ),
        "progress-bar": (record, config) => {
            const value = record?.[config?.dataIndex];
            return (
                <ProgressBarColumnView
                    minProgress={config?.startProgressFrom ?? 0}
                    defaultText={config?.defaultValue ?? "Not Available"}
                    value={value ? value.toFixed(config?.decimalPlaces ?? 0) : 0}
                    barColor={getSOHColor(value)}
                    record={record}
                    config={config}
                    unitPostFix={config?.unitPostfix ?? ""}
                />
            );
        },
    }), [onBatteryIDClick]);

    // ─── Column setup ─────────────────────────────────────────────────────────
    const allColumns = useMemo(() => {
        if (!tableConfig.columnsConfig || !tableConfig.columnOrdering) return [];
        return getColumnsConfig({
            tableColumns: tableConfig.columnsConfig,
            tableColumnOrder: tableConfig.columnOrdering,
            shouldShowActionMenu: () => false, // no edit/action columns
            shouldShowColumn,
            customRenderers,
        });
    }, [tableConfig, customRenderers, shouldShowColumn]);

    useEffect(() => {
        if (allColumns.length === 0) return;

        const sorted = [...allColumns].sort((a, b) => {
            const order = (c) => (c.fixed === "left" ? 0 : c.fixed === "right" ? 2 : 1);
            return order(a) - order(b);
        });
        const sliced = sorted.slice(0, MAX_COLUMN_SELECTION_COUNT);
        const defaultCols = [
            ...sliced.filter(c => c.fixed !== "right"),
            ...sliced.filter(c => c.fixed === "right"),
        ];

        const saved = AppCache.getItem(COLUMN_SETTING_STORAGE_KEY) || [];
        // Strip any saved keys that are no longer in allColumns (e.g. feature-gated columns).
        const validSaved = saved.filter(key => allColumns.some(c => c.dataIndex === key));
        const initial = validSaved.length
            ? allColumns.filter(c => validSaved.includes(c.dataIndex))
            : defaultCols;
        const initialKeys = validSaved.length
            ? validSaved
            : defaultCols.map(c => c.dataIndex);

        setDefaultSelectedColumns(defaultCols.map(c => c.dataIndex));
        setColumns(initial);
        setSelectedColumns(initialKeys);
        setVisibleColumnKeys(initialKeys);
        setColumnOrder(initial.map(c => c.headerLabel || c.title));
    }, [allColumns]);

    // Sync columns to Redux so CSV download works (same shape as Coulomb inventory)
    useEffect(() => {
        if (allColumns.length === 0) return;
        dispatch(SetDigitalTwinsColumns([{ name: "Digital Twins Columns", columns: allColumns }]));
    }, [allColumns, dispatch]);

    // ─── Filter setup ─────────────────────────────────────────────────────────
    useEffect(() => {
        const filterDefs = tableConfig.filters;
        if (!filterDefs?.length) return;
        const tabFilters = filterDefs.filter(f => !f.tabs || f.tabs.includes(0));
        fetchFiltersDataObject(tabFilters, filters).then(obj => {
            const sorted = Object.fromEntries(
                Object.entries(obj).sort(([, a], [, b]) => a.label.localeCompare(b.label))
            );
            setFilters(sorted);
        });
    }, [tableConfig.filters]);

    // ─── Data fetching ────────────────────────────────────────────────────────
    const getFilterConfigsMap = useCallback(() => {
        const map = {};
        (tableConfig.filters || []).forEach(f => {
            if (f.displayType === "compositeSelectFilter" && f.subFilters) {
                f.subFilters.forEach(sf => { map[sf.key] = sf; });
            } else {
                map[f.key] = f;
            }
        });
        return map;
    }, [tableConfig.filters]);

    const fetchData = useCallback(async (params = {}) => {
        if (!visibleColumnKeys.length) return;

        const searchID = searchTerm?.selectedSearchColumn || "searchID";
        const filterConfigsMap = getFilterConfigsMap();
        const partialParams = extractPartialConnectivityParams(selectedFilters, filterConfigsMap);
        const partialKeys = getPartialConnectivityFilterKeys(selectedFilters);
        const regularFilters = Object.keys(selectedFilters).reduce((acc, key) => {
            if (!partialKeys.includes(key)) acc[key] = selectedFilters[key];
            return acc;
        }, {});

        setIsLoading(true);
        const speedInColumns = visibleColumnKeys.includes("ext_wb_vehicle_speed");
        const bmsStatusImplicit = speedInColumns && !visibleColumnKeys.includes("batteryStatus");
        const apiColumns = bmsStatusImplicit ? [...visibleColumnKeys, "batteryStatus"] : visibleColumnKeys;
        const res = await getDigitalTwinList(
            {
                filters: regularFilters,
                pageNumber,
                rowsPerPage,
                sortBy: sorting[0]?.sortBy,
                orderBy: sorting[0]?.orderBy,
                batteryID: null,
                startDate: null,
                endDate: null,
                [searchID]: searchTerm?.selectedDevices || [],
                columns: apiColumns,
                ...(partialParams?.canDataTypes && { canDataTypes: partialParams.canDataTypes }),
                ...(partialParams?.canDataDateRange && { canDataDateRange: partialParams.canDataDateRange }),
                ...params,
            },
            canUseGlobalAdvancedFilter ? (globalFilters || {}) : {}
        );

        if (res.responseStatus.code === 200) {
            const rawRows = res.response.data || [];
            const processedRows = rawRows.map((row) => {
                const speed = parseFloat(row.ext_wb_vehicle_speed);
                if (!isNaN(speed)) {
                    const isDischarging = row.batteryStatus === "Discharging";
                    if (!isDischarging || speed > 250) {
                        return { ...row, ext_wb_vehicle_speed: 0 };
                    }
                }
                return row;
            });
            setTableData(processedRows);
            const newTotal = res.response.total?.total || 0;
            dispatch(SetDigitalTwinsTotal(newTotal));
            const hasActiveFilters = (searchTerm?.selectedDevices?.length > 0) || Object.keys(selectedFilters).length > 0;
            if (!hasActiveFilters) {
                setUnfilteredTotal(newTotal);
            }
        } else if (res.responseStatus.code === -2) {
            dispatch(SetSessionExpired(true));
            setTableData([]);
            dispatch(SetDigitalTwinsTotal(0));
        } else {
            setTableData([]);
            dispatch(SetDigitalTwinsTotal(0));
        }
        setIsLoading(false);
    }, [visibleColumnKeys, pageNumber, rowsPerPage, sorting, selectedFilters, searchTerm, globalFilters, canUseGlobalAdvancedFilter]);

    useEffect(() => {
        if (!visibleColumnKeys.length) return;
        const current = JSON.stringify({ pageNumber, selectedFilters, searchTerm, sorting, visibleColumnKeys, globalFilters });
        if (current !== JSON.stringify(lastFetchParams.current)) {
            lastFetchParams.current = JSON.parse(current);
            fetchData();
        }
    }, [pageNumber, selectedFilters, searchTerm, sorting, visibleColumnKeys, globalFilters]);

    useEffect(() => {
        if (pageNumber !== 1) dispatch(SetDigitalTwinsPageNumber(1));
    }, [selectedFilters, searchTerm, sorting, globalFilters]);

    // ─── Row selection ────────────────────────────────────────────────────────
    const onSelectChange = (record, selected) => {
        const keys = new Set(totalSelectedRowKeys);
        const batteryIds = new Set(totalSelectedRowBatteryIds);
        if (selected) { keys.add(record.id); batteryIds.add(record.batteryID); }
        else { keys.delete(record.id); batteryIds.delete(record.batteryID); if (isSelectAll) setIsSelectAll(false); }
        setTotalSelectedRowKeys(keys);
        setTotalSelectedRowBatteryIds(batteryIds);
    };

    const onSelectAllChange = (selected, selectedRows) => {
        setIsSelectAll(selected);
        if (selected) {
            setTotalSelectedRowKeys(new Set(selectedRows.map(r => r.id)));
            setTotalSelectedRowBatteryIds(new Set(selectedRows.map(r => r.batteryID)));
        } else {
            setTotalSelectedRowKeys(new Set());
            setTotalSelectedRowBatteryIds(new Set());
        }
    };

    const getTotalRowSelection = () => Array.from(totalSelectedRowKeys);
    const getTotalRowSelectionCount = () => isSelectAll ? total : totalSelectedRowKeys.size;

    const filtersForSelectAll = () => {
        const searchDevices = searchTerm?.selectedDevices || [];
        if (searchDevices.length === 0) return { ...selectedFilters };
        const searchColumn = searchTerm?.selectedSearchColumn || "searchID";
        return { ...selectedFilters, [searchColumn]: searchDevices };
    };

    const getCSVData = async () => {
        // The backend returns download-csv rows keyed by headerLabel, not by dataIndex.
        const speedCol = columns.find((c) => c.dataIndex === "ext_wb_vehicle_speed");
        const speedKey = speedCol?.headerLabel || speedCol?.title || null;

        const speedInDownload = !!speedCol;
        const bmsStatusInColumns = columns.some((c) => c.dataIndex === "batteryStatus");
        const bmsStatusImplicit = speedInDownload && !bmsStatusInColumns;

        let csvColumns = columns;
        let bmsStatusHeaderKey = null;
        if (speedInDownload) {
            if (bmsStatusImplicit) {
                const bmsColFromAll = allColumns.find((c) => c.dataIndex === "batteryStatus");
                const bmsCol = bmsColFromAll || { dataIndex: "batteryStatus", headerLabel: "BMS Status", title: "BMS Status" };
                bmsStatusHeaderKey = bmsCol.headerLabel || bmsCol.title;
                csvColumns = [...columns, bmsCol];
            } else {
                const bmsCol = columns.find((c) => c.dataIndex === "batteryStatus");
                bmsStatusHeaderKey = bmsCol?.headerLabel || bmsCol?.title || null;
            }
        }

        const res = await getDigitalTwinData(
            {
                resourceIDs: getTotalRowSelection(),
                selectAll: isSelectAll,
                actionType: "download-csv",
                filters: filtersForSelectAll(),
                resourceType: "digital-twin",
                columnOrder,
                columns: JSON.stringify(csvColumns),
            },
            canUseGlobalAdvancedFilter ? (globalFilters || {}) : {}
        );
        if (res?.responseStatus?.code === 200) {
            const rows = res.response.data || [];
            return rows.map((row) => {
                let processedRow = row;
                if (speedKey) {
                    const speed = parseFloat(row[speedKey]);
                    if (!isNaN(speed)) {
                        const isDischarging = bmsStatusHeaderKey ? row[bmsStatusHeaderKey] === "Discharging" : false;
                        if (!isDischarging || speed > 250) {
                            processedRow = { ...processedRow, [speedKey]: 0 };
                        }
                    }
                }
                if (bmsStatusImplicit && bmsStatusHeaderKey && bmsStatusHeaderKey in processedRow) {
                    const { [bmsStatusHeaderKey]: _removed, ...rest } = processedRow;
                    processedRow = rest;
                }
                return processedRow;
            });
        }
        if (res?.responseStatus?.code === -2) dispatch(SetSessionExpired(true));
        return [];
    };

    const applySelectedColumns = (newKeys) => {
        const filtered = allColumns.filter(c => newKeys.includes(c.dataIndex));
        setColumns(filtered);
        setSelectedColumns(newKeys);
        AppCache.setItem(COLUMN_SETTING_STORAGE_KEY, newKeys);
        setVisibleColumnKeys(newKeys);
        setColumnOrder(filtered.map(c => c.headerLabel || c.title));
    };

    const onColumnOrderChange = (cols) => {
        setColumnOrder(cols.map(c => c.headerLabel || c.title));
    };

    const shouldShowFilteredCount = () => {
        if (isLoading) return false;
        return (searchTerm?.selectedDevices?.length > 0) || Object.keys(selectedFilters).length > 0;
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    if (pageContentLoading) {
        return (
            <Box sx={{ height: "100vh", bgcolor: ThemeProperties.backgroundPurpleDarker }}>
                <Loader />
            </Box>
        );
    }

    const tabLabel = t(orgLangSettings.digitalTwinLabel);

    return (
        <ScreenContainer pagesContent={pageContent}>
            <div style={{ marginLeft: "-55px" }}>
                <OuterBoxV2>
                    <InnerBox>
                        {/* Header: page title + global filter (only if custom platform defines globalFilter) */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <PageHeaderKPI
                                name={t("inventory:page_header")}
                                skipRightSide={true}
                                nonClickableKpis={[]}
                                connectedBatteries={0}
                            />
                            {canUseGlobalAdvancedFilter && globalFilterConfig && (
                                <Box>
                                    <GlobalFilter
                                        allFleets={allFleets}
                                        allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                                        selectedFleetIds={globalFilters?.fleets || []}
                                        selectedLocations={globalFilters?.hierarchicalLocation}
                                        onChange={handleFleetAndLocationChange}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* Tab bar — single tab matching the Coulomb inventory style */}
                        <Box sx={{
                            display: "flex",
                            position: "relative",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 2,
                            mb: 2,
                            borderBottom: "1px solid #E4E3E3",
                            marginLeft: "-2rem",
                            marginRight: "-2rem",
                        }}>
                            <TabWithColorDigitalTwins
                                ChangeTheTab={() => {}}
                                tabs={[{
                                    id: 1,
                                    slug: "digital_twins",
                                    name: tabLabel,
                                    count: unfilteredTotal !== null ? unfilteredTotal : total,
                                    color: ThemeProperties.red_400,
                                }]}
                            />
                        </Box>

                        {/* Table */}
                        <BasicTabPanel value={0} index={0}>
                            <GenericDraggableTableView
                                title={tabLabel}
                                count={unfilteredTotal !== null ? unfilteredTotal : total}
                                showPrimaryButton={false}
                                applySearch={(devices, col) => setSearchTerm({ selectedDevices: devices, selectedSearchColumn: col })}
                                searchableFields={tableConfig.searchableFields || undefined}
                                disableSearch={false}
                                disableDownloads={false}
                                showEmailComposer={false}
                                getTotalRowSelection={getTotalRowSelection}
                                getTotalRowSelectionCount={getTotalRowSelectionCount}
                                getCSVData={getCSVData}
                                availableEmailDomains={[]}
                                filters={filters}
                                selectedFilters={selectedFilters}
                                applyFilters={setSelectedFilters}
                                initialColumns={allColumns}
                                selectedColumns={selectedColumns}
                                applySelectedColumns={applySelectedColumns}
                                filteredCount={shouldShowFilteredCount() ? total : 0}
                                tableColumns={columns}
                                tableData={tableData}
                                onColumnOrderChange={onColumnOrderChange}
                                columnOrder={columnOrder}
                                isLoading={isLoading}
                                pageNumber={pageNumber}
                                rowsPerPage={rowsPerPage}
                                total={total}
                                applySort={(key, order) => setSorting([{ sortBy: key, orderBy: order }])}
                                onPageChange={(page) => dispatch(SetDigitalTwinsPageNumber(page))}
                                emptyMessage="No matches found for filters applied"
                                maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
                                defaultSelectedColumns={defaultSelectedColumns}
                                initialSorting={sorting[0] || {}}
                                onClearSelection={() => {
                                    setTotalSelectedRowKeys(new Set());
                                    setTotalSelectedRowBatteryIds(new Set());
                                    setIsSelectAll(false);
                                }}
                                rowSelection={{
                                    selectedRowKeys: Array.from(totalSelectedRowKeys),
                                    onSelect: onSelectChange,
                                    onSelectAll: onSelectAllChange,
                                }}
                            />
                        </BasicTabPanel>
                    </InnerBox>
                </OuterBoxV2>
            </div>
        </ScreenContainer>
    );
}

export default CustomInventoryPage;
